import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Alert } from "../components/ui/Alert";
import { ThemeSwitcher } from "../components/theme/ThemeSwitcher";
import { AuthBrandingHeader } from "../components/auth/AuthBrandingHeader";
import { usePublicConfig } from "../hooks/usePublicConfig";
import { useBrandingSettings } from "../lib/settings/useBrandingSettings";

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: publicConfig } = usePublicConfig();
  const branding = useBrandingSettings();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mismatch, setMismatch] = useState(false);

  // No token — redirect to forgot password
  if (!token) {
    return (
      <div className="surface-grid flex min-h-screen items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <AuthBrandingHeader
            title="Invalid link"
            subtitle="This reset link is missing or malformed"
            authPages={publicConfig?.authPages}
            branding={branding.settings}
          />
          <Card className="glass-card">
            <Alert variant="warning" className="mb-4">
              No reset token found. Please request a new password reset link.
            </Alert>
            <Link to="/forgot-password">
              <Button type="button" size="lg" className="w-full">
                Request new link
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMismatch(false);
    setError(null);

    if (password !== confirmPassword) {
      setMismatch(true);
      return;
    }

    setLoading(true);

    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.message ?? "Unable to reset password. The link may have expired.");
        return;
      }
      navigate("/login?reset=success", { replace: true });
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="surface-grid flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <AuthBrandingHeader
          title="Set a new password"
          subtitle="Choose a secure password for your account"
          authPages={publicConfig?.authPages}
          branding={branding.settings}
        />

        <Card className="glass-card">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Reset password</h2>
              <p className="text-sm text-muted-foreground">Enter your new password below.</p>
            </div>
            <ThemeSwitcher compact />
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive-foreground">
              {error}
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">New password</label>
              <Input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                autoFocus
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">Confirm password</label>
              <Input
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  setMismatch(false);
                }}
                autoComplete="new-password"
                required
              />
              {mismatch && (
                <p className="text-xs text-destructive-foreground">Passwords do not match.</p>
              )}
            </div>
            <Button type="submit" size="lg" className="w-full" disabled={loading}>
              {loading ? "Resetting…" : "Reset password"}
            </Button>
          </form>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            Remember your password? <Link to="/login">Sign in</Link>
          </p>
        </Card>
      </div>
    </div>
  );
};
